import React, { useState, useEffect, useMemo, memo } from 'react';

// --- مكون كرت المنتج المطور بتصميم عالمي ---
const ProductCard = memo(({ p, isRtl, getImageUrl, addToCart }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imageUrl = getImageUrl(p.image_url);

  return (
    <div key={p.id} style={styles.card} className="product-card">
      <div style={styles.imgWrap}>
        {/* تأثير التحميل الخلفي الهادئ */}
        {!isLoaded && <div className="loading-skeleton" style={styles.skeleton}></div>}
        
        <img 
          src={imageUrl} 
          alt={isRtl ? p.name_ar : p.name_en} 
          style={{ 
            ...styles.img, 
            opacity: isLoaded ? 1 : 0, 
            transform: isLoaded ? 'scale(1)' : 'scale(0.98)',
          }} 
          decoding="async"
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          onError={(e) => { 
            e.target.src = 'https://via.placeholder.com/300?text=Yam+Product';
            setIsLoaded(true); 
          }}
        />
      </div>
      <div style={{ ...styles.cardInfo, textAlign: isRtl ? 'right' : 'left' }}>
          <h4 style={styles.pName}>{isRtl ? p.name_ar : p.name_en}</h4>
          <p style={styles.pDesc}>{isRtl ? p.description_ar : p.description_en}</p>
          <div style={styles.cardFooter}>
              <div style={styles.priceContainer}>
                <span style={styles.pPrice}>{p.price}</span>
                <span style={styles.currency}>EGP</span>
              </div>
              <button onClick={() => addToCart(p)} style={styles.addBtn} className="add-to-cart-btn">
                {isRtl ? 'إضافة +' : 'Add +'}
              </button>
          </div>
      </div>
    </div>
  );
});

const ProductShop = ({ lang, filterBrand }) => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); 
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', city: '' });
  const [loading, setLoading] = useState(true);
  const [isEgypt, setIsEgypt] = useState(true); 
  const [visibleCount, setVisibleCount] = useState(12);

  const isRtl = lang === 'ar';
  const API_BASE_URL = 'https://www.yam-ii.com/yam_api';

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/get_products.php`);
        const result = await response.json();
        if (result.status === 'success') {
          setIsEgypt(result.isEgypt);
          setProducts(Array.isArray(result.products) ? result.products : []);
        } else if (Array.isArray(result)) {
          setProducts(result);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => { setVisibleCount(12); }, [searchTerm, filterBrand]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const name = isRtl ? (p.name_ar || "") : (p.name_en || "");
      const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBrand = filterBrand ? (p.brand_en === filterBrand.name) : true;
      return matchesSearch && matchesBrand;
    });
  }, [products, searchTerm, filterBrand, isRtl]);

  const productsToShow = useMemo(() => filteredProducts.slice(0, visibleCount), [filteredProducts, visibleCount]);

  const loadMore = (e) => {
    if(e) e.preventDefault();
    setVisibleCount(prev => prev + 12);
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item));
    } else {
      setCart([...cart, { ...product, qty: 1, displayName: isRtl ? product.name_ar : product.name_en }]);
    }
    setIsCartOpen(true);
  };

  const updateQty = (id, delta) => {
    setCart(cart.map(item => item.id === id ? { ...item, qty: Math.max(1, item.qty + delta) } : item));
  };

  const removeFromCart = (id) => setCart(cart.filter(item => item.id !== id));
  
  const totalPrice = useMemo(() => cart.reduce((sum, item) => sum + (parseFloat(item.price || 0) * item.qty), 0), [cart]);

const submitOrder = async () => {
    if (cart.length === 0) return;
    if (!customerInfo.name || !customerInfo.phone || !customerInfo.city) {
      alert(isRtl ? 'برجاء ملء بيانات الشحن كاملة' : 'Please fill all shipping info');
      return;
    }
    const orderItemsText = cart.map(item => `• ${item.displayName} (x${item.qty})`).join('%0A');
    const whatsappMessage = `*طلب جديد من متجر يام*%0A%0A*الاسم:* ${customerInfo.name}%0A*الهاتف:* ${customerInfo.phone}%0A*العنوان:* ${customerInfo.city}%0A%0A*المنتجات:*%0A${orderItemsText}`;
    window.location.href = `https://api.whatsapp.com/send?phone=201070107397&text=${whatsappMessage}`;
  };

  const getImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/300';
    if (url.startsWith('http')) return url;
    const fileName = url.split('/').pop().split('.')[0] + '.webp';
    return `${API_BASE_URL}/uploads/optimized/${fileName}`;
  };

  if (loading) return <div style={styles.loader}>{isRtl ? 'جاري التحميل...' : 'Loading...'}</div>;

  return (
    <section id="yamshop" style={styles.section}>
      <div style={styles.headerContainer}>
        <div style={styles.titleWrap}>
          <h2 style={styles.title}>{filterBrand ? (isRtl ? filterBrand.ar : filterBrand.name) : (isRtl ? 'متجر يام' : 'Yam Store')}</h2>
          <p style={styles.subtitle}>{isRtl ? 'جودة تليق بك' : 'Quality you deserve'}</p>
        </div>
        
        <div style={styles.searchWrapper}>
          <span style={isRtl ? styles.searchIconRtl : styles.searchIcon}>🔍</span>
          <input 
            type="text" 
            placeholder={isRtl ? "ابحث عن منتجك المفضل..." : "Search your favorite..."} 
            style={styles.searchInput} 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </div>

        <div onClick={() => setIsCartOpen(true)} style={styles.cartBtnFixed}>
          <div style={styles.cartIconBox}>
             🛒 {cart.length > 0 && <span style={styles.badge}>{cart.length}</span>}
          </div>
          <div style={styles.priceBox}>{totalPrice} <small>EGP</small></div>
        </div>
      </div>

      <div style={styles.grid}>
        {productsToShow.map((p, index) => (
          <ProductCard key={`${p.id}-${index}`} p={p} isRtl={isRtl} getImageUrl={getImageUrl} addToCart={addToCart} />
        ))}
      </div>

      {filteredProducts.length > visibleCount && (
        <div style={{ textAlign: 'center', marginTop: '60px' }}>
          <button onClick={loadMore} style={styles.loadMoreBtn} className="load-more">
            {isRtl ? 'تصفح المزيد' : 'Explore More'}
          </button>
        </div>
      )}

      {/* Cart Drawer */}
      <div style={{ ...styles.overlay, visibility: isCartOpen ? 'visible' : 'hidden', opacity: isCartOpen ? 1 : 0 }} onClick={() => setIsCartOpen(false)}>
        <div style={{ ...styles.drawer, transform: isCartOpen ? 'translateX(0)' : (isRtl ? 'translateX(100%)' : 'translateX(-100%)'), [isRtl ? 'right' : 'left']: 0 }} onClick={e => e.stopPropagation()}>
          <div style={styles.drawerHeader}>
            <h3 style={{margin:0, color: '#1a3a5a'}}>{isRtl ? 'سلة التسوق' : 'My Cart'}</h3>
            <button onClick={() => setIsCartOpen(false)} style={styles.closeBtn}>✕</button>
          </div>
          <div style={styles.drawerBody}>
            {cart.length === 0 ? (
              <div style={styles.emptyTxt}><div style={{fontSize: '50px'}}>🛒</div><p>{isRtl ? 'سلتك فارغة حالياً' : 'Your cart is empty'}</p></div>
            ) : (
              <>
                {cart.map(item => (
                  <div key={item.id} style={styles.cartItem}>
                    <div style={{flex: 1}}>
                      <div style={{fontWeight:'700', color: '#1a3a5a'}}>{item.displayName}</div>
                      <div style={{color:'#d4af37', fontWeight: 'bold'}}>{item.price * item.qty} EGP</div>
                    </div>
                    <div style={styles.qtyControl}>
                      <button onClick={() => updateQty(item.id, -1)} style={styles.qBtn}>-</button>
                      <span style={{fontWeight: 'bold'}}>{item.qty}</span>
                      <button onClick={() => updateQty(item.id, 1)} style={styles.qBtn}>+</button>
                    </div>
                    <button onClick={() => removeFromCart(item.id)} style={styles.delBtn}>🗑️</button>
                  </div>
                ))}
                <div style={styles.formSection}>
                  <p style={{fontWeight: 'bold', marginBottom: '10px'}}>{isRtl ? 'بيانات التوصيل' : 'Shipping Details'}</p>
                  <input type="text" placeholder={isRtl ? 'الاسم بالكامل' : 'Full Name'} style={styles.input} value={customerInfo.name} onChange={e => setCustomerInfo({...customerInfo, name: e.target.value})} />
                  <input type="tel" placeholder={isRtl ? 'رقم الهاتف' : 'Phone Number'} style={styles.input} value={customerInfo.phone} onChange={e => setCustomerInfo({...customerInfo, phone: e.target.value})} />
                  <input type="text" placeholder={isRtl ? 'المدينة / العنوان' : 'City / Address'} style={styles.input} value={customerInfo.city} onChange={e => setCustomerInfo({...customerInfo, city: e.target.value})} />
                </div>
              </>
            )}
          </div>
          <div style={styles.drawerFooter}>
            <div style={styles.footerRow}><span>{isRtl ? 'الإجمالي النهائي' : 'Subtotal'}</span><span style={{fontSize: '1.2rem', color: '#1a3a5a'}}>{totalPrice} EGP</span></div>
            <button onClick={submitOrder} disabled={cart.length === 0} style={{...styles.submitBtn, backgroundColor: cart.length === 0 ? '#eee' : '#27ae60', cursor: cart.length === 0 ? 'not-allowed' : 'pointer'}}>
              {isRtl ? 'تأكيد الطلب' : 'Confirm Order  '}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .product-card { transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1); background: #fff; }
        .product-card:hover { transform: translateY(-10px); boxShadow: 0 20px 30px rgba(0,0,0,0.08) !important; }
        .add-to-cart-btn { transition: all 0.2s; }
        .add-to-cart-btn:active { transform: scale(0.95); }
        .load-more:hover { background: #1a3a5a !important; color: #fff !important; }
        @keyframes skeletonPulse { 0% { background-color: #f0f0f0; } 50% { background-color: #f9f9f9; } 100% { background-color: #f0f0f0; } }
        .loading-skeleton { animation: skeletonPulse 1.5s infinite ease-in-out; }
      `}</style>
    </section>
  );
};

const styles = {
  section: { padding: '60px 5%', backgroundColor: '#fcfdfe' },
  headerContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px', flexWrap: 'wrap', gap: '25px' },
  titleWrap: { flex: 1, minWidth: '200px' },
  title: { color: '#1a3a5a', fontSize: '2rem', fontWeight: '800', margin: 0, letterSpacing: '-0.5px' },
  subtitle: { color: '#999', margin: '5px 0 0 0' },
  searchWrapper: { position: 'relative', flex: 1, maxWidth: '450px', minWidth: '280px' },
  searchInput: { width: '100%', padding: '14px 20px 14px 45px', borderRadius: '12px', border: '1px solid #ececec', outline: 'none', fontSize: '15px', transition: '0.3s', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' },
  searchIcon: { position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#bbb' },
  searchIconRtl: { position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', color: '#bbb' },
  cartBtnFixed: { display: 'flex', alignItems: 'center', background: '#fff', borderRadius: '14px', cursor: 'pointer', boxShadow: '0 10px 25px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #f0f0f0' },
  cartIconBox: { background: '#1a3a5a', padding: '14px 20px', position: 'relative', color: '#fff' },
  badge: { position: 'absolute', top: '6px', right: '8px', background: '#d4af37', color: '#fff', fontSize: '10px', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', border: '2px solid #1a3a5a' },
  priceBox: { padding: '0 20px', fontWeight: '800', color: '#1a3a5a', fontSize: '15px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '25px' },
  card: { borderRadius: '24px', overflow: 'hidden', border: '1px solid #f3f3f3', display: 'flex', flexDirection: 'column' },
  imgWrap: { width: '100%', aspectRatio: '1/1', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px', backgroundColor: '#fff', position: 'relative' },
  img: { width: '100%', height: '100%', objectFit: 'contain', transition: 'all 0.5s ease' },
  skeleton: { position: 'absolute', inset: 0, borderRadius: '24px' },
  cardInfo: { padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' },
  pName: { fontSize: '1.15rem', color: '#1a3a5a', fontWeight: '700', marginBottom: '8px', lineHeight: '1.4' },
  pDesc: { color: '#777', fontSize: '0.85rem', marginBottom: '15px', lineHeight: '1.5' },
  cardFooter: { marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  priceContainer: { display: 'flex', alignItems: 'baseline', gap: '4px' },
  pPrice: { color: '#1a3a5a', fontSize: '1.4rem', fontWeight: '900' },
  currency: { color: '#d4af37', fontSize: '0.75rem', fontWeight: 'bold' },
  addBtn: { padding: '10px 18px', background: '#1a3a5a', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '0.9rem' },
  loader: { textAlign: 'center', padding: '100px', fontWeight: 'bold', color: '#1a3a5a' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, transition: '0.4s', backdropFilter: 'blur(5px)' },
  drawer: { position: 'fixed', top: 0, height: '100%', width: '100%', maxWidth: '400px', background: '#fff', zIndex: 10001, transition: 'transform 0.4s cubic-bezier(0.77, 0, 0.175, 1)', display: 'flex', flexDirection: 'column', boxShadow: '-10px 0 30px rgba(0,0,0,0.1)' },
  drawerHeader: { padding: '25px', borderBottom: '1px solid #f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeBtn: { background: '#f8f8f8', border: 'none', fontSize: '16px', cursor: 'pointer', width: '35px', height: '35px', borderRadius: '50%' },
  drawerBody: { flex: 1, overflowY: 'auto', padding: '25px' },
  cartItem: { display: 'flex', gap: '15px', padding: '15px', background: '#fbfbfb', borderRadius: '18px', marginBottom: '12px', alignItems: 'center', border: '1px solid #f0f0f0' },
  qtyControl: { display: 'flex', alignItems: 'center', gap: '12px', background: '#fff', borderRadius: '8px', padding: '5px 10px', border: '1px solid #eee' },
  qBtn: { border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', color: '#1a3a5a' },
  delBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', opacity: 0.6 },
  formSection: { marginTop: '30px', background: '#f9f9f9', padding: '20px', borderRadius: '18px' },
  input: { width: '100%', padding: '14px', marginBottom: '10px', border: '1px solid #e0e0e0', borderRadius: '10px', fontSize: '14px', outline: 'none' },
  drawerFooter: { padding: '25px', borderTop: '1px solid #f5f5f5' },
  footerRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontWeight: '800' },
  submitBtn: { width: '100%', padding: '16px', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: 'bold', fontSize: '1rem', transition: '0.3s' },
  emptyTxt: { textAlign: 'center', marginTop: '80px', color: '#ccc' },
  loadMoreBtn: { padding: '14px 40px', background: 'transparent', color: '#1a3a5a', border: '2px solid #1a3a5a', borderRadius: '14px', cursor: 'pointer', fontWeight: '800', transition: '0.3s' }
};

export default ProductShop;